import { Component, Input, Output, OnInit, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PagingData } from '../../../../service/proxy';

@Component({
    imports: [CommonModule, FormsModule],
    selector: 'pageable-table',
    templateUrl: './pageable-table.component.html',
    styleUrl: './pageable-table.component.scss'
})

export class PageableTableComponent<T extends {}> implements OnInit {
    @Input()
    public items: T[] = [];

    @Input()
    public selectedItems: T[] = [];

    @Input()
    public paging: PagingData = new PagingData();

    @Input()
    public columns: { [key: string]: string } = {};

    @Input()
    public glyphColumn: string = '';

    @Input()
    public glyphTooltipColumn: string = '';

    @Input()
    public switchField: string | null = null; // Field that should be displayed as a switch

    @Input()
    public useSwitch: boolean = false; // Whether to enable switches

    @Output()
    public refreshData = new EventEmitter<PagingData>()

    @Input()
    public showAdd: boolean = false;

    @Input()
    public showEdit: boolean = false;

    @Input()
    public showCopy: boolean = false;

    @Input()
    public showView: boolean = false;

    @Input()
    public showDelete: boolean = false;

    @Input()
    public deleteIconClass: string = 'fa-trash';

    @Input()
    public showRowGlyph: boolean = false;

    @Input()
    public selectionEnabled: boolean = false;

    @Input()
    public allowMultiSelect: boolean = false;

    @Input()
    public highlightedIds: number[] = [];

    @Output()
    public addItem = new EventEmitter<number>()

    @Output()
    public editItem = new EventEmitter<number>()

    @Output()
    public copyItem = new EventEmitter<number>()

    @Output()
    public viewItem = new EventEmitter<T>()

    @Output()
    public deleteItem = new EventEmitter<number>()

    @Output()
    public selectionChanged = new EventEmitter<T[]>()

    @Output()
    public switchChanged = new EventEmitter<{ item: T, newValue: boolean }>();

    constructor() {
    }

    ngOnInit(): void {
        this.triggerRefreshData();
    }

    triggerRefreshData(): void {
        this.refreshData.emit(this.paging);
    }

    getColumnKeys(): string[] {
        return Object.keys(this.columns);
    }

    getColumnValue(item: T, column: string): any {
        return (item as Record<string, any>)[column];
    }

    onSwitchToggle(item: T): void {
        if (!this.switchField) return;

        const currentValue = this.getColumnValue(item, this.switchField);
        const newValue = !currentValue;

        (item as Record<string, any>)[this.switchField] = newValue;

        this.switchChanged.emit({ item, newValue });
    }

    onColumnHeaderClick(column: string): void {
        if (this.paging.sortColumn === column) {
            this.flipSortDirection();
        }
        else {
            this.paging.pageNo = 1;
            this.paging.sortColumn = column;
            this.paging.sortDirection = 'ASC'
        }

        this.paging.pageNo = 1;
        this.triggerRefreshData();
    }

    flipSortDirection(): void {
        if (this.paging.sortDirection === 'ASC') {
            this.paging.sortDirection = 'DESC';
        }
        else {
            this.paging.sortDirection = 'ASC';
        }
    }

    getSortIconClass(column: string): string {
        if (!this.paging.sortColumn || this.paging.sortColumn !== column) {
            return '';
        }

        if (!this.paging.sortDirection || this.paging.sortDirection === 'ASC') {
            return '↓';
        }
        else {
            return '↑';
        }
    }

    getPageNumber(): string {
        return `${this.paging.pageNo || 1} of ${this.paging.pageCount || 1}`;
    }

    triggerAdd(item: T) {
        this.addItem.emit(this.getColumnValue(item, 'id'));
    }

    triggerEdit(item: T) {
        this.editItem.emit(this.getColumnValue(item, 'id'));
    }

    triggerCopy(item: T) {
        this.copyItem.emit(this.getColumnValue(item, 'id'));
    }

    triggerView(item: T) {
        this.viewItem.emit(item);
    }

    triggerDelete(item: T) {
        this.deleteItem.emit(this.getColumnValue(item, 'id'));
    }

    onDoubleClick(item: T) {
        if (this.showEdit) {
            this.editItem.emit(this.getColumnValue(item, 'id'));
        } else if (this.showView) {
            this.viewItem.emit(item);
        }
    }

    triggerSelectionChanged() {
        this.selectionChanged.emit(this.selectedItems);
    }

    selectItem(item: T) {
        if (!this.selectionEnabled) {
            this.selectedItems.length = 0;
            return;
        }

        const index = this.selectedItems.indexOf(item);

        if (!this.allowMultiSelect) {
            if (index !== -1) {
                this.selectedItems.splice(index, 1);
            }
            else {
                this.selectedItems.length = 0;
                this.selectedItems.push(item);
            }
        }
        else {
            if (index !== -1) {
                this.selectedItems.splice(index, 1);
            }
            else {
                this.selectedItems.push(item);
            }
        }

        this.triggerSelectionChanged();
    }

    pageNext() {
        if (this.paging.pageNo < this.paging.pageCount) {
            this.paging.pageNo++;
            this.selectedItems.length = 0;
            this.triggerRefreshData();
        }
    }

    pagePrevious() {
        if (this.paging.pageNo > 1) {
            this.paging.pageNo--;
            this.selectedItems.length = 0;
            this.triggerRefreshData();
        }
    }

    pageSizeChanged(pageSize: number): void {
        this.paging.pageNo = 1;
        this.triggerRefreshData();
    }

    isSelected(item: T): boolean {
        return this.selectedItems.includes(item);
    }

    isHighlighted(item: T): boolean {
        const id = this.getColumnValue(item, 'id');
        return this.highlightedIds.includes(id);
    }

    hasExpired(item: T): boolean {
        return (item as any)?.isExpired === true;
    }
}
